import { useId, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { QuoteRow } from "@/hooks/useQuotes";
import { useCreateQuoteTemplate } from "@/hooks/useQuoteTemplates";
import { quoteToTemplateInput } from "@/lib/quoteTemplates";

interface SaveTemplateDialogProps {
  quote: QuoteRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** ⋯ → Save as template: the old panel's Save-as-Template dialog, moved (#1559). */
export function SaveTemplateDialog({ quote, open, onOpenChange }: SaveTemplateDialogProps) {
  const createTemplate = useCreateQuoteTemplate();
  const [templateName, setTemplateName] = useState("");
  const [templateNotes, setTemplateNotes] = useState("");
  const fieldId = useId();
  const idFor = (name: string) => `${fieldId}-${name}`;

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim() || !quote.line_items?.length) return;
    try {
      await createTemplate.mutateAsync(quoteToTemplateInput(quote, { name: templateName, notes: templateNotes }));
    } catch {
      // The hook's onError already toasted; keep the dialog open with what was typed.
      return;
    }
    setTemplateName("");
    setTemplateNotes("");
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) { setTemplateName(""); setTemplateNotes(""); }
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display">Save as Template</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={idFor("template-name")} className="text-xs font-medium">Template Name *</Label>
            <Input
              id={idFor("template-name")}
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g. Wedding Buffet Standard"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={idFor("template-notes")} className="text-xs font-medium">Template notes</Label>
            <Textarea
              id={idFor("template-notes")}
              value={templateNotes}
              onChange={(e) => setTemplateNotes(e.target.value)}
              rows={2}
              placeholder="Pre-fills the client-facing notes on new quotes from this template"
            />
            <p className="text-xs text-muted-foreground">
              These notes pre-fill every quote made from this template, so keep one client's details out.
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            Saves the {quote.line_items?.length ?? 0} line items, service type, guest count and event type from this quote. The quote's own notes aren't copied.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              size="sm"
              onClick={handleSaveAsTemplate}
              disabled={!templateName.trim() || createTemplate.isPending}
            >
              {createTemplate.isPending ? "Saving..." : "Save Template"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
